import Image from 'next/image';
import Link from 'next/link';

import { Button, Container, Text } from '@/atoms';

import { isBotonContacto, useBotonContacto } from '@/hooks/useBotonContacto';
import {
  blockSpacing,
  getRealUrl,
  sectionBgProps,
  textStyleClass,
} from '@/utils';

export const ComponentRevisadasCtaHipoteca = ({
  Titulo,
  Descripcion,
  Ilustracion,
  CtaBoton,
  TituloEstilo,
  DescripcionEstilo,
  FondoSeccion,
  // Fondo del banner interior. Default `bg-accent` (comportamiento histórico);
  // si el tenant lo pica, el inline style gana sobre la clase. Distinto de
  // `FondoSeccion`, que tiñe la sección completa que envuelve al banner.
  FondoBanner,
  // Variante compacta (la del simulador de hipoteca, Figma 2182:6693): además
  // de la ilustración pequeña (~58px vs ~181px), el banner pasa a "tarjeta"
  // (rounded-card, padding uniforme 48px, gap 48px).
  IlustracionCompacta,
  // Estilo del banner cuando NO es compacto. Default = tarjeta histórica
  // (rounded-custom + bg-primary-50 + md:p-8, e1f26a9~). `pildora` activa el
  // look del Figma HoM (bg-accent, md:rounded-full, padding asimétrico).
  // OJO: bg-accent cae a bgcolor cuando el tenant no define accentbg — en
  // tenants legacy con bgcolor oscuro la píldora saldría azul marino.
  //
  // En la query GraphQL el campo viaja aliasado como `EstiloCtaHipoteca`
  // (los enums de Strapi son un tipo por componente y dos `Estilo` en la
  // misma dynamic zone no se pueden mergear); se acepta cualquiera de los
  // dos nombres para no depender de que cada página aplique transformer.
  Estilo,
  EstiloCtaHipoteca,
  // Espaciado vertical del bloque. `legacy=''` → sin Espaciado el bloque no
  // añade block-padding (comportamiento histórico de CtaHipoteca), así los
  // tenants/páginas que no lo configuran renderizan idéntico.
  Espaciado,
  noContainer = false,
}: any) => {
  const { modal, abrirContacto } = useBotonContacto();
  const estilo = EstiloCtaHipoteca ?? Estilo;
  const banner = (
    <div
      className={
        IlustracionCompacta
          ? // Tarjeta (simulador): radio de card + padding/gap uniformes 48px.
            'rounded-card bg-accent flex flex-col items-center gap-6 p-6 md:flex-row md:gap-12 md:p-12'
          : estilo === 'pildora'
            ? // Píldora (Figma HoM, ficha de vivienda).
              'rounded-custom bg-accent flex flex-col items-center gap-6 overflow-hidden p-6 md:flex-row md:gap-10 md:rounded-full md:py-0 md:pr-20 md:pl-10'
            : // Tarjeta histórica (comportamiento legacy sin knob).
              'rounded-custom bg-primary-50 flex flex-col items-center gap-6 p-6 md:flex-row md:gap-10 md:p-8'
      }
      style={FondoBanner ? { backgroundColor: FondoBanner } : undefined}
    >
      {Ilustracion?.url && (
        <div
          className={`relative shrink-0 ${
            IlustracionCompacta
              ? 'size-14 md:size-16'
              : estilo === 'pildora'
                ? 'size-32 md:h-44 md:w-[181px]'
                : 'size-32 md:size-44'
          }`}
        >
          <Image
            src={getRealUrl(Ilustracion.url)}
            alt={Ilustracion.alternativeText ?? ''}
            fill
            sizes={
              IlustracionCompacta
                ? '(min-width: 768px) 64px, 56px'
                : '(min-width: 768px) 181px, 128px'
            }
            className="object-contain"
          />
        </div>
      )}

      <div className="flex flex-1 flex-col gap-2 text-center md:text-left">
        {Titulo && (
          <Text
            as="h2"
            className={`text-headline text-headline-h3 ${
              textStyleClass(TituloEstilo) ?? ''
            }`}
          >
            {Titulo}
          </Text>
        )}
        {Descripcion && (
          <Text
            className={`text-body text-body-l ${
              textStyleClass(DescripcionEstilo) ?? ''
            }`}
          >
            {Descripcion}
          </Text>
        )}
      </div>

      {CtaBoton?.Texto && isBotonContacto(CtaBoton) && (
        <>
          <Button
            type="button"
            size={CtaBoton.Size}
            variant={CtaBoton.Variante}
            className="shrink-0"
            onClick={() => abrirContacto(CtaBoton)}
          >
            {CtaBoton.Texto}
          </Button>
          {modal}
        </>
      )}
      {CtaBoton?.Texto && CtaBoton?.Link && !isBotonContacto(CtaBoton) && (
        <Link href={CtaBoton.Link} prefetch={false} passHref legacyBehavior>
          <Button
            as="a"
            size={CtaBoton.Size}
            variant={CtaBoton.Variante}
            className="shrink-0"
          >
            {CtaBoton.Texto}
          </Button>
        </Link>
      )}
    </div>
  );

  return noContainer ? (
    banner
  ) : (
    <Container
      wrapper
      classNameWrapper={blockSpacing(Espaciado, '')}
      {...sectionBgProps(FondoSeccion)}
    >
      {banner}
    </Container>
  );
};

ComponentRevisadasCtaHipoteca.displayName = 'ComponentRevisadasCtaHipoteca';
